import { Button, Icon, Tooltip } from 'gu-ui-kit'
import PropTypes from 'prop-types'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import useGeneratePathI18n from 'utils/hooks/useGeneratePathI18n'
import { Link } from 'react-router-dom'
import { useGetAccountAction } from 'modules/Services/hooks/useGetAccountAction'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'
import styles from 'modules/Services/Account/network-account.module.scss'
import { ArchiveButton, CustomButton, DeleteButton, RefundButton, } from 'modules/Services/Account/subcomponents'

const ActionLink = ({
  action,
  i18n,
  id,
  type,
  auto_replenishment,
  handleClick = () => {},
  name,
  extra_info,
  noLabel = false,
}) => {
  const { t } = useTranslation(['services'])
  const config = useGetAccountAction(action)
  const pathGen = useGeneratePathI18n()
  const breakpoints = useBreakpoint()

  const buttonProps = useMemo(() => {
    if (!config) return

    let props = { action, id, as: Button, size: 'l', noLabel }

    const url = pathGen(config.url, {
      i18n,
      accountId: id,
      networkType: type,
    })

    switch (action) {
      case 'deposit':
        return {
          ...props,
          variant: 'primary',
          component: Link,
          to: url,
          size: breakpoints.l ? 'm' : 'l',
          startIcon: (
            <Icon
              name={config.icon}
              size={'m'}
            />
          ),
          fullWidth: breakpoints.fbs,
        }

      case 'deposit_creation_fee':
        return {
          ...props,
          variant: 'primary',
          component: Link,
          to: url,
          size: breakpoints.l ? 'm' : 'l',
          startIcon: (
            <Icon
              name={config.icon}
              size={'m'}
            />
          ),
        }

      case 'refresh':
        return {
          ...props,
          variant: 'ghost',
          grey: true,
          link: true,
          component: 'a',
          size: 's',
          href: url,
          color: 'inherit',
          startIcon: <Icon name={config.icon} />,
        }

      case 'delete':
        return {
          ...props,
          variant: 'ghost',
          action: action,
          grey: true,
          link: true,
          size: 's',
          title: t(`account_list.extra_actions.${action}`),
          startIcon: <Icon name={config.icon} />,
          as: DeleteButton,
          color: 'inherit',
          handleClick,
          name,
        }
      case 'archive':
        return {
          ...props,
          variant: 'ghost',
          action: action,
          grey: true,
          link: true,
          size: 's',
          title: t(`account_list.extra_actions.${action}`),
          startIcon: <Icon name={config.icon} />,
          as: ArchiveButton,
          color: 'inherit',
          handleClick,
          name,
        }
      case 'refund':
        return {
          ...props,
          variant: 'ghost',
          grey: true,
          link: false,
          as: RefundButton,
          title: t(`account_list.extra_actions.${action}`),
          to: url,
          size: breakpoints.l ? 'm' : 'l',
          startIcon: (
            <Icon
              name={config.icon}
              size={'m'}
            />
          ),
          color: 'inherit',
          extra_info,
        }
      case 'auto_replenish':
        return {
          ...props,
          variant: 'secondary',
          as: CustomButton,
          size: breakpoints.l ? 'm' : 'l',
          grey: true,
          link: false,
          to: url,
          color: 'inherit',
          icon: config.icon,
          title: t(`account_list.extra_actions.${action}`),
          startIcon: (
            <Icon
              name={config.icon}
              size={'m'}
            />
          ),
          additionalIcon: breakpoints.xl && auto_replenishment?.is_available && (
            <Icon
              {...(auto_replenishment?.is_enabled
                ? {
                    name: 'status done',
                    color: 'Label-Confirm',
                  }
                : {
                    name: 'status warning2',
                    color: 'Label-Error-Primary',
                  })}
              size={breakpoints.l ? 'sm' : 'm'}
              style={{
                top: '-7px',
                right: '-7px',
              }}
            />
          ),
          endIcon: breakpoints.xl ? null : auto_replenishment?.is_available ? (
            <>
              <Tooltip
                title={t(
                  `services::account_list.auto_replenish_status.${
                    auto_replenishment?.is_enabled ? 'disable' : 'enable'
                  }`,
                )}
              >
                <Icon
                  {...(auto_replenishment?.is_enabled
                    ? {
                        name: 'status done',
                        color: 'Label-Confirm',
                      }
                    : {
                        name: 'status warning2',
                        color: 'Label-Error-Primary',
                      })}
                  size={breakpoints.xl ? 'sm' : 'm'}
                />
              </Tooltip>
            </>
          ) : null,
        }
      case 'campaign-create':
      case 'campaigns':
        return {
          ...props,
          variant: 'ghost',
          grey: true,
          link: true,
          color: 'inherit',
          component: Link,
          to: url,
          size: 's',
          startIcon: <Icon name={config.icon} />,
        }

      case 'access':
        return {
          ...props,
          variant: 'ghost',
          grey: true,
          link: true,
          component: Link,
          to: url,
          size: 's',
          color: 'inherit',
          startIcon: <Icon name={config.icon} />,
        }

      default:
        return {
          ...props,
          variant: 'ghost',
          component: 'a',
          grey: true,
          link: true,
          size: 's',
          href: url,
          color: 'inherit',
          startIcon: <Icon name={config.icon} />,
        }
    }
  }, [action, id, i18n, breakpoints])

  if (!config) {
    return null
  }

  const { as: Component, ...rest } = buttonProps

  return (
    <Component
      className={styles['network-account__action-button']}
      {...rest}
    >
      {t('services::account_list.extra_actions.' + action)}
    </Component>
  )
}

ActionLink.propTypes = {
  action: PropTypes.string,
  i18n: PropTypes.string,
  id: PropTypes.string,
  type: PropTypes.string,
  balance: PropTypes.shape({
    amount: PropTypes.number,
    currency: PropTypes.string,
  }),
  auto_replenishment: PropTypes.shape({
    is_available: PropTypes.bool,
    is_enabled: PropTypes.bool,
  }),
  handleClick: PropTypes.func,
  name: PropTypes.string,
  extra_info: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.oneOfType([PropTypes.oneOf([undefined]), PropTypes.oneOf([null])]),
  ]),
  noLabel: PropTypes.bool,
}

export default ActionLink
